/* FilterRenderer.js */

"use strict";
const EventEmitter = require("events");
const _ = require("lodash");
const InputCheckboxControl = require("@gluenetworks/input_checkbox_control");

class FilterRenderer extends EventEmitter{
    constructor(model, options, advancedFilterData) {
        super();
        this._model = model;
        this._rootElem = document.createElement("entity-grid-gl-filter");

        this.initialValue = _.get(options, 'string', '');
        this.initialIsRegEx = _.get(options, 'isRegEx', false);

        this._filterIcon = this._createFilterIcon(!_.isEmpty(advancedFilterData));
        this._regexCheckbox = this._createRegexCheckbox(this.initialIsRegEx);
        this._filterInput = this._createFilterInput(this.initialValue);
        this._clearBtn = this._createClearBtn();

        //bindings
        this._setExpanded = this._setExpanded.bind(this);
        this._setCollapsed = this._setCollapsed.bind(this);
        this._regexStateChange = this._regexStateChange.bind(this);
        this._onRootBlur = this._onRootBlur.bind(this);
        this._inputChange = _.debounce(this._inputChange.bind(this), 400);
        this._inputKeyUp = this._inputKeyUp.bind(this);
        this._closeFilter = this._closeFilter.bind(this);
        this._onFilterDataChange = this._onFilterDataChange.bind(this);
        this._onAdvancedFilterChanged = this._onAdvancedFilterChanged.bind(this);
        this._toggleIntputValidity = this._toggleIntputValidity.bind(this);

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on('FilterDataChange', this._onFilterDataChange);
        this._model.on('AdvancedFilterChanged', this._onAdvancedFilterChanged);
        this._model.on('FilterInputIsValid', this._toggleIntputValidity);
        this._filterIcon.addEventListener("click", this._setExpanded);
        this._filterInput.addEventListener("input", this._inputChange);
        this._filterInput.addEventListener("keyup", this._inputKeyUp);
        this._clearBtn.addEventListener("click", this._closeFilter);
        this._rootElem.addEventListener("blur", this._onRootBlur, true);

        this._regexCheckbox.on("UserValue", this._regexStateChange);
    }
    _unregisterListeners() {
        this._model.removeListener('FilterDataChange', this._onFilterDataChange);
        this._model.removeListener('AdvancedFilterChanged', this._onAdvancedFilterChanged);
        this._model.removeListener('FilterInputIsValid', this._toggleIntputValidity);
        this._filterIcon.removeEventListener("click", this._setExpanded);
        this._filterInput.removeEventListener("input", this._inputChange);
        this._filterInput.removeEventListener("keyup", this._inputKeyUp);
        this._clearBtn.removeEventListener("click", this._closeFilter);
        this._rootElem.removeEventListener("blur", this._onRootBlur, true);

        this._regexCheckbox.removeListener("UserValue", this._regexStateChange);
    }

    _emitFilterStringChange(filterData, force) {
        this.emit('FilterStringChange', filterData, force);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        return Promise.all([this._unregisterListeners(), this._regexCheckbox.shutdown()])
            .then(() => {
                this._filterIcon = null;
                this._regexCheckbox = null;
                this._filterInput = null;
                this._clearBtn = null;

                this._rootElem.remove();
                this._rootElem = null;
                this._logger = null;
                this._model = null;
            })
            .catch(err => this._logger.warn({ error: err }, "filterRender.shutdown() error"));
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._toggleExpansion(this.initialValue);

        const inputsWrapper = document.createElement("div");
        inputsWrapper.classList.add("inputs-wrapper");

        const filterInputWrapper = document.createElement("div");
        filterInputWrapper.classList.add("filter-input-wrapper");

        return new Promise((resolve /*,reject*/) => {
            this._rootElem.appendChild(inputsWrapper);
            inputsWrapper.appendChild(filterInputWrapper);
                filterInputWrapper.appendChild(this._filterInput);
                filterInputWrapper.appendChild(this._clearBtn);
            inputsWrapper.appendChild(this._regexCheckbox.renderer._rootElem);
            this._rootElem.appendChild(this._filterIcon);

            resolve();
        }).catch(err => console.warn(err));
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }
    get model() {
        return this.getModel();
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    _toggleExpansion(isExpanded){
        if (isExpanded) {
            this._setExpanded();
        } else {
            this._setCollapsed();
        }
    }
    _setCollapsed() {
        this._rootElem.classList.remove("expanded");
        this._rootElem.classList.add("collapsed");

        this._rootElem.parentElement.classList.remove("filter-expanded");
        this._rootElem.parentElement.classList.add("filter-collapsed");
    }
    _setExpanded() {
        this._rootElem.classList.remove("collapsed");
        this._rootElem.classList.add("expanded");

        this._rootElem.parentElement.classList.remove("filter-collapsed");
        this._rootElem.parentElement.classList.add("filter-expanded");

        this._filterInput.focus();
    }

    _toggleIntputValidity(inputIsValid = true) {
        this._filterInput.classList.toggle('invalid', !inputIsValid);
    }

    _getInputValue() {
        return this._filterInput.value;
    }
    _setInputValue(value = ''){
        this._filterInput.value = value;
    }

    _getCheckboxValue() {
        return this._regexCheckbox.getValue();
    }
    _setCheckboxValue(value){
        this._regexCheckbox.setValue(Boolean(value));
    }
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onFilterDataChange({string, isRegEx} = {}){
        this._setInputValue(string);
        this._setCheckboxValue(isRegEx);
    }

    _onAdvancedFilterChanged(advancedFilterQuery) {
        this._filterIcon.setToggled(!_.isEmpty(advancedFilterQuery));
    }

    _inputChange(e) {
        this._setFilterInputValue(e.target.value);
    }

    _inputKeyUp(e) {
        if (e.which === 27) { //Escape is pressed
            this._closeFilter();
        }

        if (e.which === 13) { //Enter is pressed
            this._userChangeFilterData(undefined, undefined, true);
        }
    }

    _regexStateChange(state) {
        this._userChangeFilterData(undefined, state, true);
    }

    _onRootBlur(e) {
        const relatedTarget = e.relatedTarget;
        const keepExpandedElements = [this._filterInput, this._clearBtn, this._regexCheckbox.renderer._rootElem];

        if (!keepExpandedElements.includes(relatedTarget) && this._getInputValue() === '') {
            this._setCollapsed();
        }
    }

    _closeFilter(){
        this._setInputValue("");
        this._setCheckboxValue(false);
        this._setCollapsed();
        this._toggleIntputValidity(true);

        this._userChangeFilterData('', false, false);
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _createFilterInput(value = '') {
        const filterInput = document.createElement("input");
        filterInput.value = String(value);
        filterInput.placeholder = "   ";
        filterInput.classList.add("filter-input");
        return filterInput;
    }

    _createClearBtn() {
        const clearBtn = document.createElement("button");
        clearBtn.classList.add("clear-btn");
        clearBtn.innerHTML = "&#x2716;";
        return clearBtn;
    }

    _createFilterIcon(toggled) {
        const filterIcon = document.createElement("toggle-icon-gl");
        filterIcon.dataset.icon = "common.filter";
        filterIcon.setAttribute("title", "Filter");
        filterIcon.setAttribute("data-dark-theme", "true");
        filterIcon.setToggled(toggled);
        return filterIcon;
    }

    _createRegexCheckbox(state = false) {
        return new InputCheckboxControl({
            label: "RegEx",
            labelShown: true,
            value: state,
            darkTheme: true,
        });
    }

    _setFilterInputValue(string) {
        if (this._getInputValue() !== string) {
            this._setInputValue(string);
        }

        string = _.trimStart(string); //only trim the start, as we want to keep trailing spaces for next word

        this._userChangeFilterData(string);
    }

    _userChangeFilterData(string, isRegEx, force = false){
        string = string || this._getInputValue() || '';
        isRegEx = isRegEx || this._getCheckboxValue() || false;
        if (string) {
            this._filterIcon.setToggled(false);
        }
        this._emitFilterStringChange({string, isRegEx}, force);
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = FilterRenderer;
